/**
 * GroupFormLayout
 *
 * Custom form layout for Group entities with progressive disclosure.
 *
 * - Standard mode: Identity, Group Configuration, Membership Filter (enumerated),
 *   External banner (AD-managed), Ownership.
 * - Admin mode: adds AD/Hybrid Identifiers, System/Compilation, Legacy Filter sections.
 *
 * ManagedType drives conditional visibility:
 *   Manual    -- Filter section hidden
 *   Enumerated -- Filter section shown with FilterScope + Filter
 *   External  -- Read-only banner, all fields informational
 */

import React, { useState } from 'react';
import { Alert, Box, Text, Collapse, UnstyledButton, Group } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconShieldLock } from '@tabler/icons-react';
import { getEntityValueFromARef } from '../../../types';
import type { FormAttribute } from '../EntityUpsertForm.types';
import { getAttributeName } from '../../../utils/schemaAttributeUtils';

export interface GroupFormLayoutProps {
    schemaAttributes: FormAttribute[];
    formData: Record<string, any>;
    onFieldChange: (fieldName: string, value: any) => void;
    renderField: (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) => React.ReactNode;
    disabled: boolean;
    isStandard?: boolean;
}

function normKey(raw: string): string {
    return (raw ?? '').toString().trim().toLowerCase().replace(/^\$\./, '');
}

function findAttr(attrs: FormAttribute[], key: string): FormAttribute | undefined {
    return attrs.find(a => normKey((a.name ?? a.id ?? '').toString()) === key);
}

function renderOneField(
    attr: FormAttribute,
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: GroupFormLayoutProps['renderField'],
): React.ReactNode {
    const name = getAttributeName(attr);
    const value = name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : '';
    const handleChange = (v: unknown) => onFieldChange(name, v);
    return renderField(attr, value ?? '', handleChange);
}

function resolveManagedType(formData: Record<string, any>): string {
    const mt = formData.ManagedType ?? formData.managedType ?? '';
    if (typeof mt === 'string') return mt.toLowerCase();
    if (mt && typeof mt === 'object') {
        const val = mt.DisplayName || mt.displayName || mt.Id || mt.id || '';
        return (val as string).toLowerCase();
    }
    return '';
}

const IDENTITY_FIELDS = ['displayname', 'description', 'email', 'accountname'];
const CONFIG_FIELDS = ['type', 'scope', 'managedtype'];
const FILTER_FIELDS = ['filterscope', 'filter'];
const OWNERSHIP_FIELDS = ['owner', 'owners'];
const AD_HYBRID_FIELDS = ['onpremid', 'entraid', 'objectid', 'objectsid', 'entryid'];
const SYSTEM_FIELDS = ['id', 'entitytype', 'managed', 'compilationstate', 'sqlstatement', 'sqlstatementexplicit'];
const LEGACY_FIELDS = ['legacyfilter', 'legacyfilterschope', 'legacyfilterscope', 'legacyfilterattributes', 'legacyfilterfunctions', 'legacyfilterliterals'];
const MEMBERSHIP_FIELDS = ['member', 'memberof', 'membertransitive'];

const ALL_CATEGORIZED = new Set([
    ...IDENTITY_FIELDS, ...CONFIG_FIELDS, ...FILTER_FIELDS,
    ...OWNERSHIP_FIELDS, ...AD_HYBRID_FIELDS, ...SYSTEM_FIELDS,
    ...LEGACY_FIELDS, ...MEMBERSHIP_FIELDS,
]);

function CollapsibleSection({
    title,
    defaultOpen = true,
    children,
}: {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Box mb="sm">
            <UnstyledButton
                onClick={() => setOpen(o => !o)}
                w="100%"
                py="xs"
            >
                <Group gap="xs" align="center">
                    {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    <Text fw={600} size="sm" c="dimmed">{title}</Text>
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Box mt={4}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

function renderFieldGroup(
    attrs: FormAttribute[],
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: GroupFormLayoutProps['renderField'],
): React.ReactNode {
    if (attrs.length === 0) return null;
    return (
        <>
            {attrs.map((attr, i) => (
                <Box key={(attr.name ?? attr.id ?? `fg-${i}`).toString()} mb="xs">
                    {renderOneField(attr, formData, onFieldChange, renderField)}
                </Box>
            ))}
        </>
    );
}

function collectAttrs(schemaAttributes: FormAttribute[], keys: string[]): FormAttribute[] {
    const result: FormAttribute[] = [];
    for (const key of keys) {
        const attr = findAttr(schemaAttributes, key);
        if (attr) result.push(attr);
    }
    return result;
}

function hasLegacyData(formData: Record<string, any>): boolean {
    const lf = formData.LegacyFilter ?? formData.legacyFilter ?? '';
    return typeof lf === 'string' && lf.trim().length > 0;
}

export const GroupFormLayout: React.FC<GroupFormLayoutProps> = ({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
    disabled: _disabled,
    isStandard = false,
}) => {
    const managedType = resolveManagedType(formData);
    const isExternal = managedType === 'external';
    const isEnumerated = managedType === 'enumerated';

    const identityAttrs = collectAttrs(schemaAttributes, IDENTITY_FIELDS);
    const configAttrs = collectAttrs(schemaAttributes, CONFIG_FIELDS);
    const filterAttrs = collectAttrs(schemaAttributes, FILTER_FIELDS);
    const ownershipAttrs = collectAttrs(schemaAttributes, OWNERSHIP_FIELDS);
    const membershipAttrs = collectAttrs(schemaAttributes, MEMBERSHIP_FIELDS);
    const adHybridAttrs = collectAttrs(schemaAttributes, AD_HYBRID_FIELDS);
    const systemAttrs = collectAttrs(schemaAttributes, SYSTEM_FIELDS);
    const legacyAttrs = collectAttrs(schemaAttributes, LEGACY_FIELDS);

    const uncategorized = schemaAttributes.filter(a => {
        const key = normKey((a.name ?? a.id ?? '').toString());
        return !ALL_CATEGORIZED.has(key);
    });

    return (
        <Box>
            {isExternal && (
                <Alert
                    color="blue"
                    variant="light"
                    icon={<IconShieldLock size={18} />}
                    mb="md"
                    title="Active Directory Managed Group"
                >
                    This group is managed externally by Active Directory. Changes should be made in AD and will sync automatically.
                </Alert>
            )}

            {/* Identity */}
            {identityAttrs.length > 0 && (
                <CollapsibleSection title="Identity" defaultOpen>
                    {renderFieldGroup(identityAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Group Configuration */}
            {configAttrs.length > 0 && (
                <CollapsibleSection title="Group Configuration" defaultOpen>
                    {renderFieldGroup(configAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Membership Filter -- only when Enumerated */}
            {isEnumerated && filterAttrs.length > 0 && (
                <CollapsibleSection title="Membership Filter" defaultOpen>
                    {renderFieldGroup(filterAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Membership (Member, MemberOf, MemberTransitive) */}
            {membershipAttrs.length > 0 && (
                <CollapsibleSection title="Membership" defaultOpen={false}>
                    {renderFieldGroup(membershipAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Ownership */}
            {ownershipAttrs.length > 0 && (
                <CollapsibleSection title="Ownership" defaultOpen={false}>
                    {renderFieldGroup(ownershipAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* AD / Hybrid Identifiers -- admin only */}
            {!isStandard && adHybridAttrs.length > 0 && (
                <CollapsibleSection title="AD / Hybrid Identifiers" defaultOpen={false}>
                    {renderFieldGroup(adHybridAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* System / Compilation -- admin only */}
            {!isStandard && systemAttrs.length > 0 && (
                <CollapsibleSection title="System / Compilation" defaultOpen={false}>
                    {renderFieldGroup(systemAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Legacy Filter -- admin only, when legacy data present */}
            {!isStandard && hasLegacyData(formData) && legacyAttrs.length > 0 && (
                <CollapsibleSection title="Legacy Filter" defaultOpen={false}>
                    {renderFieldGroup(legacyAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Filter fields shown in admin mode when not Enumerated (hidden in standard or already shown above) */}
            {!isStandard && !isEnumerated && filterAttrs.length > 0 && (
                <CollapsibleSection title="Filter (inactive)" defaultOpen={false}>
                    {renderFieldGroup(filterAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Uncategorized attributes -- admin only */}
            {!isStandard && uncategorized.length > 0 && (
                <CollapsibleSection title="Other Attributes" defaultOpen={false}>
                    {renderFieldGroup(uncategorized, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}
        </Box>
    );
};
